import React, { useState } from "react";
import { AlertCircle, Camera, Trash2, Shield, Info } from "lucide-react";
import { Input } from "../../components/ui/input2";
import { Button } from "../../components/ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import FormField from "../../components/ui/FormField";
import SectionHeader from "../../components/ui/SectionHeader";

import { useOutletContext } from "react-router-dom";
import { SettingsOutletContextType } from "../Settings";
import { updateProfile, removeBusinessAccount } from "../../services/settings";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import PageHeader from "../../components/ui/PageHeader";

const ProfileSettings = () => {
  const toast = useToast();
  const { settingsData, refreshSettings } =
    useOutletContext<SettingsOutletContextType>();
  const { user, updateUser } = useAuth();
  interface ProfileFormData {
    name: string;
    email: string;
    password: string;
    mobile: string;
    mobileNumber: string;
  }

  const [formData, setFormData] = useState<ProfileFormData>({
    name: "",
    email: "",
    password: "",
    mobile: "+1",
    mobileNumber: "",
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  React.useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        mobile: user.calling_code || "+1",
        mobileNumber: user.mobile || "",
      });
      setUploadedImage(user.user_image_url || user.user_image || null);
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name as keyof ProfileFormData]: value,
    }));
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSaving(true);
    setErrors({});
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password || undefined,
        calling_code: formData.mobile,
        mobile_number: formData.mobileNumber,
        image: uploadedImage || undefined,
      };

      // Log payload to check image is included
      console.log("Profile update payload:", {
        ...payload,
        image: uploadedImage
          ? (uploadedImage.substring(0, 50) + "..." + (uploadedImage.length > 50 ? ` (${uploadedImage.length} chars)` : ""))
          : undefined,
        hasImage: !!uploadedImage,
        imageType: uploadedImage?.startsWith('data:') ? 'base64' : 'url',
      });

      const response = await updateProfile(payload);

      // Update user data in AuthContext with the updated profile data
      // Use user_image_url - if uploadedImage is a base64 string, keep it; otherwise use it as URL or existing user_image_url
      const updatedUser = {
        ...user,
        name: formData.name,
        email: formData.email,
        calling_code: formData.mobile,
        mobile: formData.mobileNumber,
        // If uploadedImage is a base64 string, keep it; otherwise use it as URL or existing user_image_url
        user_image_url: (uploadedImage && uploadedImage.startsWith('data:'))
          ? uploadedImage
          : (uploadedImage || user?.user_image_url),
        image: user?.image, // Keep existing image field if needed
      };
      updateUser(updatedUser);

      toast.success("Profile updated successfully!");
      refreshSettings();
      console.log("Profile updated successfully");
    } catch (error: any) {
      console.error("Failed to update profile", error);
      toast.error("Could not update profile. Please check your data and try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAccount = () => {
    setShowRemoveModal(true);
  };

  const handleConfirmRemove = async () => {
    setDeleting(true);
    try {
      const response = await removeBusinessAccount();
      console.log("Account removal response:", response);

      if (response.success) {
        setShowRemoveModal(false);
        toast.success(response.message || "Account removed successfully");
        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
      }
    } catch (error: any) {
      console.error("Failed to remove account", error);
      toast.error("An error occurred while trying to remove the account. Please try again later.");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelRemove = () => {
    setShowRemoveModal(false);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      console.log("Image file selected:", {
        name: file.name,
        size: file.size,
        type: file.type,
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setUploadedImage(base64);
        console.log("Image converted to base64, length:", base64.length);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setImageFile(file);
      console.log("Image file dropped:", {
        name: file.name,
        size: file.size,
        type: file.type,
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setUploadedImage(base64);
        console.log("Image converted to base64, length:", base64.length);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageRemove = () => {
    setUploadedImage(null);
    setImageFile(null);
  };

  if (!settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading profile settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <PageHeader
        title="Profile Settings"
        description="Manage your account information and preferences"
        variant="card"
        mobileActionsInHeader={true}
        actions={
          <Button
            onClick={handleRemoveAccount}
            variant="destructive"
            className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 px-5 py-2.5 text-sm font-medium transition-all duration-200 flex items-center gap-2"
          >
            <Trash2 size={16} />
            Remove Account
          </Button>
        }
      />

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Picture */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
            {/* Card Header */}
            <div className="bg-[#885ABB] px-6 py-4">
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <Camera size={18} className="text-white" />
                Profile Picture
              </h3>
            </div>

            {/* Card Body */}
            <div className="p-6 flex-1 flex flex-col">
              {/* Image Upload Area */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative group cursor-pointer transition-all duration-200 ${isDragging ? "scale-[1.02]" : ""
                  }`}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="profile-upload"
                />
                <label
                  htmlFor="profile-upload"
                  className="block cursor-pointer"
                >
                  <div
                    className={`relative w-full h-52 rounded-xl overflow-hidden border transition-all ${uploadedImage
                      ? "border-[#885ABB]"
                      : "border-dashed border-gray-300 hover:border-gray-400"
                      } ${isDragging ? "border-[#885ABB] bg-gray-50" : "bg-gray-100"
                      }`}
                  >
                    {uploadedImage ? (
                      <>
                        <img
                          src={uploadedImage}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                          <div className="text-white text-center">
                            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-2">
                              <Camera size={24} />
                            </div>
                            <p className="text-sm font-medium">Change Photo</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <div className="w-20 h-20 rounded-full bg-gray-50 border-2 border-dashed border-gray-300 flex items-center justify-center mb-4">
                          <Camera size={32} className="text-gray-400" />
                        </div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">
                          Upload Photo
                        </p>
                        <p className="text-xs text-gray-500 px-4 text-center">
                          Drag & drop or click to browse
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          JPG, PNG or GIF
                        </p>
                      </div>
                    )}
                  </div>
                </label>

                {uploadedImage && (
                  <button
                    onClick={handleImageRemove}
                    className="absolute -top-3 -right-3 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 z-10"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              {/* Image Info */}
              <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex gap-2">
                  <Info
                    size={16}
                    className="text-[#885ABB] flex-shrink-0 mt-0.5"
                  />
                  <p className="text-xs text-purple-700 leading-relaxed">
                    Recommended: Square image, at least 400x400px for best
                    quality
                  </p>
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSave}
                className="mt-6 bg-[#885ABB] hover:bg-[#7748A4] text-white px-6 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column - Account Information */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
            {/* Card Header */}
            <div className="bg-[#885ABB] px-6 py-4">
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <Shield size={18} className="text-white" />
                Account Information
              </h3>
            </div>

            {/* Card Body */}
            <div className="p-6 flex-1">
              <div className="space-y-5">
                {/* Personal Information Section */}
                <div>
                  <SectionHeader title="Personal Details" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="Full Name *"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={(name, value) => {
                        setFormData((prev) => ({
                          ...prev,
                          [name as keyof ProfileFormData]: value,
                        }));
                        if (errors.name) setErrors(prev => ({ ...prev, name: "" }));
                      }}
                      placeholder="Enter your full name"
                      error={errors.name}
                    />
                    <FormField
                      label="Email Address *"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={(name, value) => {
                        setFormData((prev) => ({
                          ...prev,
                          [name as keyof ProfileFormData]: value,
                        }));
                        if (errors.email) setErrors(prev => ({ ...prev, email: "" }));
                      }}
                      placeholder="Enter your email address"
                      error={errors.email}
                    />
                  </div>
                </div>

                {/* Security Section */}
                <div>
                  <SectionHeader title="Security" />
                  {/* Password Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <Input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter new password"
                    />
                    <p className="text-xs text-gray-500 mt-2 ml-1 flex items-center gap-1">
                      <AlertCircle size={12} />
                      Leave blank to keep current password
                    </p>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div>
                  <SectionHeader title="Contact Information" />
                  {/* Mobile Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3">
                      <Select
                        value={formData.mobile}
                        onValueChange={(value) =>
                          setFormData((prev) => ({ ...prev, mobile: value }))
                        }
                      >
                        <SelectTrigger className="w-32 rounded-lg bg-gray-100 text-sm text-gray-900 shadow-sm transition-all duration-150 ease-in-out border-transparent focus:border-gray-300 focus:ring-1 focus:ring-purple-500/40 hover:bg-gray-50 focus:bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white rounded-lg shadow-xl border border-gray-200">
                          <SelectItem value="+353">🇮🇪 +353</SelectItem>
                          <SelectItem value="+1">🇺🇸 +1</SelectItem>
                          <SelectItem value="+44">🇬🇧 +44</SelectItem>
                          <SelectItem value="+91">🇮🇳 +91</SelectItem>
                          <SelectItem value="+92">🇵🇰 +92</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="tel"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        placeholder="Enter mobile number"
                        className={errors.mobileNumber ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""}
                      />
                    </div>
                  </div>
                  {errors.mobileNumber && (
                    <p className="mt-1 text-xs text-red-500">{errors.mobileNumber}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Remove Account Confirmation Modal */}
      {showRemoveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-8">
            {/* Warning Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full border-4 border-red-400 flex items-center justify-center">
                <AlertCircle
                  className="w-12 h-12 text-red-400"
                  strokeWidth={3}
                />
              </div>
            </div>

            {/* Modal Content */}
            <div className="text-center mb-6">
              <h3 className="text-2xl font-semibold text-gray-800 mb-3">
                Are you sure?
              </h3>
              <p className="text-gray-600">
                You will not be able to recover the deleted record!
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-center gap-3">
              <Button
                onClick={handleCancelRemove}
                disabled={deleting}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmRemove}
                disabled={deleting}
                className="bg-red-500 hover:bg-red-600 text-white px-8 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? "Deleting..." : "OK"}
              </Button>
            </div>
          </div>
        </div>
      )
      }
    </div >
  );
};

export default ProfileSettings;
